/**
 * @Author: Li, Yu Jia
 * @Date: 2020-02-13 12:43:54
 * @Last Modified by: Li, Yu Jia
 * @Last Modified time: 2021-04-27 15:03:45
 */

import {
  ADD_TEMP_BACKGROUND_IMAGE,
  CHANGE_HEIGHTFITS,
  CHANGE_IMPRINTAREA_DIMENSION,
  CHANGE_LOGO_EDITING_STATUS,
  HIDE_MESSAGE_BOX,
  HIDE_WAITING_COVER,
  IMPRINT_OPTION_UI_SET,
  INITIALIZE_CHARGEDETAILS,
  INITIALIZE_USER,
  REMOVE_TEMP_BACKGROUND_IMAGE,
  SET_BEFORE_UNLOAD_LISTEN,
  SET_CANVAS_PREVIEW,
  SET_ELEMENT_DRAG_FLAG,
  SET_PARAMETER_TOKEN,
  SHOW_APPLY_TO_ALL_BUTTON,
  SHOW_MESSAGE_BOX,
  SHOW_WAITING_COVER,
  TOKENEXPIRATION_POPUP_TOGGLE,
  TOKENWARNING_POPUP_TOGGLE,
} from '../main/actions';
import { applyNoticeToggle, userImageActionType } from '../userimage/actions';
import { getLogoEditingStatus, getStudioItemType } from '../main/selectors';

import { ELEMENTS_ACTION_CHANGE_ELEMENT } from '../elements/actions';
import { SET_ALERT_INFO } from '../nextgen/actions';
import { canvasActionType } from '../canvas/actions';
import { getTempElement } from '../canvas/selectors';
import { isLogoEditing } from '../uploadimage/logics';
import { previewActionType } from '../preview/actions';
import { studioItemType } from '../main/constants';
import { templateActionType } from '../template/actions';
import { textActionType } from '../text/actions';

const getActions = (studioType) => {
  let actions = {
    keep: [
      canvasActionType.ADD_TEMP,
      canvasActionType.CHANGE_ART_COLOR,
      canvasActionType.CHANGE_CANVAS_RESIZE,
      canvasActionType.CHANGE_SELECTED_TEXT_STATUS_TO_TEMP,
      canvasActionType.RETURN_BATCH_SELECT_TEXT,
      canvasActionType.RETURN_SELECT_TEXT,
      canvasActionType.RETURN_UNSELECT_ALL_TEXT,
      canvasActionType.SET_IMAGE_UI,
      canvasActionType.UPDATE_ELEMENT_BY_STATUS,
      canvasActionType.DELETE_SAVEDART_IMAGE,
      ELEMENTS_ACTION_CHANGE_ELEMENT,
      HIDE_WAITING_COVER,
      IMPRINT_OPTION_UI_SET,
      INITIALIZE_CHARGEDETAILS,
      previewActionType.UPDATE_FULL_PREVIEW,
      SET_BEFORE_UNLOAD_LISTEN,
      SET_ELEMENT_DRAG_FLAG,
      SET_PARAMETER_TOKEN,
      SHOW_WAITING_COVER,
      TOKENEXPIRATION_POPUP_TOGGLE,
      TOKENWARNING_POPUP_TOGGLE,
      userImageActionType.RECENTART_INIT,
      userImageActionType.SAVEDART_COUNT_INIT,
      userImageActionType.SAVEDART_LIST_INIT,
      userImageActionType.SET_APPLY_NOTICE_PROMPT,
      userImageActionType.SET_APPLY_NOTICE_SCROLL,
      userImageActionType.SET_SAVED_ART_THUMBNAIL,
      userImageActionType.SET_USERIMAGE_CLONE,
      userImageActionType.SHAREDART_INIT,
      userImageActionType.STOCKART_BY_CATEGORY,
      userImageActionType.REMOVE_SAVEDART_FROM_RECENT,
      INITIALIZE_USER,
      SHOW_APPLY_TO_ALL_BUTTON,
      textActionType.CHANGE_TEXT_EDITING_STATUS,
    ],
    allow: [
      canvasActionType.ADD_ART,
      canvasActionType.ADD_TEMP,
      canvasActionType.CHANGE_CANVAS_RESIZE,
      canvasActionType.CANCEL_SELECT_ART,
      canvasActionType.CHANGE_ART_COLOR,
      canvasActionType.CHANGE_SELECTED_TEXT_STATUS_TO_TEMP,
      canvasActionType.RETURN_BATCH_SELECT_TEXT,
      canvasActionType.RETURN_SELECT_TEXT,
      canvasActionType.RETURN_UNSELECT_ALL_TEXT,
      canvasActionType.SET_IMAGE_UI,
      canvasActionType.UPDATE_ELEMENT_BY_STATUS,
      canvasActionType.UPDATE_ELEMENT_DIMENSION,
      canvasActionType.DELETE_SAVEDART_IMAGE,
      canvasActionType.CHANGE_IMAGE_UI_STATUS,
      CHANGE_LOGO_EDITING_STATUS,
      ELEMENTS_ACTION_CHANGE_ELEMENT,
      HIDE_MESSAGE_BOX,
      HIDE_WAITING_COVER,
      IMPRINT_OPTION_UI_SET,
      INITIALIZE_CHARGEDETAILS,
      previewActionType.UPDATE_FULL_PREVIEW,
      SET_BEFORE_UNLOAD_LISTEN,
      SET_ELEMENT_DRAG_FLAG,
      SET_PARAMETER_TOKEN,
      SHOW_MESSAGE_BOX,
      SHOW_WAITING_COVER, //for saved art apply
      TOKENEXPIRATION_POPUP_TOGGLE,
      TOKENWARNING_POPUP_TOGGLE,
      userImageActionType.APPLYNOTICE_TOGGLE,
      userImageActionType.RECENTART_INIT,
      userImageActionType.SAVEDART_COUNT_INIT,
      userImageActionType.SAVEDART_LIST_INIT,
      userImageActionType.SET_APPLY_NOTICE_PROMPT,
      userImageActionType.SET_APPLY_NOTICE_SCROLL,
      userImageActionType.SET_SAVED_ART_THUMBNAIL,
      userImageActionType.SET_USERIMAGE_CLONE,
      userImageActionType.SET_USERIMAGE_DIMENSION,
      userImageActionType.SHAREDART_INIT,
      userImageActionType.STOCKART_BY_CATEGORY,
      userImageActionType.REMOVE_SAVEDART_FROM_RECENT,
      INITIALIZE_USER,
      SHOW_APPLY_TO_ALL_BUTTON,
      textActionType.CHANGE_TEXT_EDITING_STATUS,
      templateActionType.ADD_RECENT_BACKGROUND,
      templateActionType.CHANGE_BACKGROUND_STEP,
      templateActionType.SAVE_CURRENT_ACTIVE_COMMON_TEMPLATE,
      ADD_TEMP_BACKGROUND_IMAGE,
      templateActionType.CLEAR_CURRENT_ACTIVE_COMMON_TEMPLATE,
      REMOVE_TEMP_BACKGROUND_IMAGE,
      templateActionType.CHANGE_TEMP_RECENT_BACKGROUND,
      canvasActionType.CHANGE_LOGO_STATUS,
      templateActionType.SELECT_TEMPLATE,
      SET_ALERT_INFO,
    ],
  };

  switch (studioType) {
    case studioItemType.nonDigital:
      actions = {
        keep: [
          ...actions.keep,
          canvasActionType.CHANGE_SLOGAN_YEAR,
          canvasActionType.DELETE_IMAGE,
          canvasActionType.SET_ITEMSTYIE_ORIGIN_SIZE,
          CHANGE_HEIGHTFITS,
          CHANGE_IMPRINTAREA_DIMENSION,
          textActionType.CHANGE_COLOR_NONDIGITAL,
          textActionType.CHANGE_NONDIGITAL_TEXT_LINE,
        ],
        allow: [
          ...actions.allow,
          canvasActionType.CHANGE_SLOGAN_YEAR,
          canvasActionType.CHANGE_TO_TEMP,
          canvasActionType.DELETE_IMAGE,
          canvasActionType.SET_ITEMSTYIE_ORIGIN_SIZE,
          canvasActionType.UPDATE_CANVAS_IMAGE,
          canvasActionType.ADD_ART_FOR_LITE,
          CHANGE_HEIGHTFITS,
          CHANGE_IMPRINTAREA_DIMENSION,
          textActionType.CHANGE_COLOR_NONDIGITAL,
          textActionType.CHANGE_NONDIGITAL_TEXT_LINE,
        ],
      };
      break;
    case studioItemType.digital:
    case studioItemType.screenPrintedDigital:
      actions = {
        keep: [...actions.keep, userImageActionType.APPLYNOTICE_TOGGLE, SET_CANVAS_PREVIEW],
        allow: [
          ...actions.allow,
          canvasActionType.CHANGE_SLOGAN_YEAR,
          canvasActionType.CHANGE_TO_TEMP,
          canvasActionType.DELETE_IMAGE,
          SET_CANVAS_PREVIEW,
        ],
      };
      break;
    default:
      break;
  }
  return actions;
};

function createDataMiddleware(extraArgument) {
  return ({ _, getState }) =>
    (next) =>
    (action) => {
      const state = getState();
      // skip if in logo/background edit panels
      if (isLogoEditing(getLogoEditingStatus(state).currentStatus)) {
        return next(action);
      }
      const tempElement = getTempElement(state);
      const studioType = getStudioItemType(state);
      const actions = getActions(studioType);
      const actionsCapture = (next, action, allowActions, keepNotice) => {
        if (allowActions.find((x) => x === action.type)) {
          if (keepNotice) {
            //KeepingNoticeStatus
            console.log(`keep:${action.type}`);
          } else {
            next(applyNoticeToggle({ show: false, keepPromptText: true }));
            console.log(`hide:${action.type}`);
          }
          return next(action);
        } else {
          next(applyNoticeToggle({ show: true, scroll: true, keepPromptText: true }));
          console.log(`captured:${action.type}`);
          return null;
        }
      };

      if (tempElement) {
        const keepNotice = actions.keep.find((x) => x === action.type);
        return actionsCapture(next, action, actions.allow, keepNotice);
      } else {
        return next(action);
      }
    };
}

const tempSelectedMiddleware = createDataMiddleware();
tempSelectedMiddleware.withExtraArgument = createDataMiddleware;

export default tempSelectedMiddleware;
